import { z } from "zod";
import {
  AiManagedGroupEffectiveStatusSchema,
  AiManagedGroupMemberSchema,
  AiManagedGroupSchema,
} from "./ai-managed-group";
import { BusinessGoalV1Schema, ExecutionModeV1Schema } from "./ai-policy";
import { metricSnapshotSchema } from "./campaign";
import {
  campaignAnalyticsGranularitySchema,
  campaignAnalyticsKpiSchema,
  campaignAnalyticsTrendPointSchema,
  csvQueryValues,
  dashboardRangeSchema,
} from "./dashboard";
import { reportingDateRangeQueryShape, validateReportingDateRange } from "./reporting";

export const AiManagedGroupAnalyticsQuerySchema = z
  .object({
    range: dashboardRangeSchema.catch("30d"),
    ...reportingDateRangeQueryShape,
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
    shopIds: csvQueryValues(z.string().min(1)),
    search: z.string().trim().max(200).catch(""),
    status: csvQueryValues(AiManagedGroupEffectiveStatusSchema),
    businessGoal: csvQueryValues(BusinessGoalV1Schema),
    executionMode: csvQueryValues(ExecutionModeV1Schema),
    attention: z.enum(["all", "needed", "clear"]).catch("all"),
  })
  .superRefine(validateReportingDateRange);
export type AiManagedGroupAnalyticsQuery = z.infer<typeof AiManagedGroupAnalyticsQuerySchema>;

export const AiManagedGroupAnalyticsListItemSchema = AiManagedGroupSchema.extend({
  shopName: z.string().nullable(),
  metrics: metricSnapshotSchema,
  pendingDecisions: z.number().int().nonnegative(),
  needsAttention: z.boolean(),
}).strict();
export type AiManagedGroupAnalyticsListItem = z.infer<typeof AiManagedGroupAnalyticsListItemSchema>;

export const AiManagedGroupAnalyticsResponseSchema = z
  .object({
    range: dashboardRangeSchema,
    granularity: campaignAnalyticsGranularitySchema,
    dataThrough: z.string().date().nullable(),
    matchedCampaigns: z.number().int().nonnegative(),
    kpis: z.array(campaignAnalyticsKpiSchema),
    trend: z.array(campaignAnalyticsTrendPointSchema),
    groups: z.array(AiManagedGroupAnalyticsListItemSchema),
    nextRunAt: z.string().datetime().nullable(),
    currency: z.string().nullable(),
    currencyApproximate: z.boolean(),
    excludedShops: z.number().int().nonnegative(),
  })
  .strict();
export type AiManagedGroupAnalyticsResponse = z.infer<typeof AiManagedGroupAnalyticsResponseSchema>;

export const AiManagedGroupLatestRunSchema = z
  .object({
    id: z.string().uuid(),
    status: z.enum(["running", "suspended", "success", "partial", "failed"]),
    startedAt: z.string().datetime(),
  })
  .strict();
export type AiManagedGroupLatestRun = z.infer<typeof AiManagedGroupLatestRunSchema>;

export const AiManagedGroupMemberAnalyticsSchema = AiManagedGroupMemberSchema.extend({
  metrics: metricSnapshotSchema,
  latestRun: AiManagedGroupLatestRunSchema.nullable(),
  pendingDecisions: z.number().int().nonnegative(),
}).strict();
export type AiManagedGroupMemberAnalytics = z.infer<typeof AiManagedGroupMemberAnalyticsSchema>;

export const AiManagedGroupActivitySchema = z
  .object({
    id: z.string().uuid(),
    campaignId: z.string().uuid().nullable(),
    campaignName: z.string().nullable(),
    jobType: z.enum(["daily_bid_optimization", "daily_search_term_mining"]),
    triggerType: z.enum(["cron", "manual"]),
    status: z.enum(["running", "suspended", "success", "partial", "failed"]),
    pendingDecisions: z.number().int().nonnegative(),
    hasDecisionRecord: z.boolean(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
  })
  .strict();
export type AiManagedGroupActivity = z.infer<typeof AiManagedGroupActivitySchema>;

export const AiManagedGroupDetailAnalyticsResponseSchema = z
  .object({
    group: AiManagedGroupSchema,
    shopName: z.string().nullable(),
    range: dashboardRangeSchema,
    granularity: campaignAnalyticsGranularitySchema,
    dataThrough: z.string().date().nullable(),
    metrics: metricSnapshotSchema,
    kpis: z.array(campaignAnalyticsKpiSchema),
    trend: z.array(campaignAnalyticsTrendPointSchema),
    members: z.array(AiManagedGroupMemberAnalyticsSchema),
    activity: z.array(AiManagedGroupActivitySchema),
    nextRunAt: z.string().datetime().nullable(),
    currency: z.string().nullable(),
  })
  .strict();
export type AiManagedGroupDetailAnalyticsResponse = z.infer<
  typeof AiManagedGroupDetailAnalyticsResponseSchema
>;
